import { useState } from "react";
import { Plus, Save, RotateCcw } from "lucide-react";
import { BulkStyleRow } from "@/components/BulkStyleRow";
// import SelectCategory from "@/components/SelectCategory";
import { useBulkStyleCreation } from "@/hooks/useBulkStyleCreation";
import { showToast } from "@/api/notification";
import axios from "axios";
import SelectApparelCategory from "@/components/SelectApparelCategory";
import { useNavigate } from "react-router-dom";
import BaseUrl from "@/BaseUrl";

export const BulkCreate = () => {
  const [isSaving, setIsSaving] = useState(false);
  const [msrCategoryId, setMsrCategoryId] = useState<string>("");
  const [categoryId, setCategoryId] = useState<string | null>("");
  const navigate = useNavigate();

  const {
    category,
    setCategory,
    rows,
    addRow,
    removeRow,
    updateRow,
    clearAll,
    validateRows,
    // getFormDataArray,
  } = useBulkStyleCreation();

  const handleAddRow = () => {
    if (!category) {
      alert("Please select a category first");
      return;
    }
    addRow();
  };

  // const handleSaveAll = async () => {
  //   if (!validateRows()) {
  //     alert("Please fill in all required fields for each style");
  //     return;
  //   }

  //   try {
  //     setIsSaving(true);
  //     const formDataArray = getFormDataArray();

  //     // Create a single FormData for bulk creation
  //     const bulkFormData = new FormData();

  //     // Add category IDs once for all styles
  //     bulkFormData.append("category_id", categoryId);
  //     bulkFormData.append("msr_category", msrCategoryId);

  //     // Add each style's data with index
  //     formDataArray.forEach((styleFormData, index) => {
  //       // Extract data from each FormData and add with index
  //       for (const [key, value] of styleFormData.entries()) {
  //         if (key !== "category_id" && key !== "msr_category") {
  //           // Add indexed field names for bulk processing
  //           bulkFormData.append(`styles[${index}][${key}]`, value);
  //         }
  //       }
  //     });

  //     // Send single request to bulk endpoint
  //     const res = await postApi(
  //       "api/style/v1/bulk-create-styles/",
  //       bulkFormData
  //     );

  //     if (res.status === 201 || res.status === 200) {
  //       alert(`Successfully created ${formDataArray.length} styles!`);

  //       // Clear all data on success
  //       // clearAll();
  //       // setCategoryId("");
  //       // setMsrCategoryId("");
  //     }
  //   } catch (error) {
  //     console.error("Error saving bulk styles:", error);
  //     alert("Error saving styles. Please try again.");
  //   } finally {
  //     setIsSaving(false);
  //   }
  // };

  const handleSaveAll = async () => {
    // Validation - same as reference code
    for (const style of rows.slice(0, -1)) {
      if (!style.name.trim()) {
        showToast({
          message: "Style name is required for all styles.",
          type: "error",
        });
        return;
      }
      if (!style.styleNumber.trim()) {
        showToast({
          message: "Style number is required for all styles.",
          type: "error",
        });
        return;
      }
      if (style.images.length === 0) {
        showToast({
          message: "At least one image is required for all styles.",
          type: "error",
        });
        return;
      }
    }

    if (!categoryId && !msrCategoryId) {
      showToast({ message: "Please select a category.", type: "error" });
      return;
    }

    const validRows = rows.filter(
      (row) =>
        row.name.trim() !== "" &&
        row.styleNumber.trim() !== "" &&
        row.tags.length !== 0 &&
        row.images.length > 0
    );

    // Validation with correct count
    if (validRows.length === 0) {
      showToast({
        message: "Please add at least one style with all required fields.",
        type: "error",
      });
      return;
    }
    try {
      setIsSaving(true);

      // Filter out empty rows - same logic as reference
      // const filteredRows = rows.filter(
      //   (row) =>
      //     row.name.trim() !== "" ||
      //     row.styleNumber.trim() !== "" ||
      //     row.images.length > 0 ||
      //     row.tags.length > 0
      // );

      const data = [];
      for (const style of rows.slice(0, -1)) {
        data.push({
          name: style.name,
          style_number: style.styleNumber,
          category_id: categoryId,
          msr_category: msrCategoryId,
          tags: style.tags.map((tag) => tag.tag_id || tag),
          images: style.images, // File objects
        });
      }

      // Send FormData request - same as reference code
      // const res = await postApi("api/style/v1/bulk-create-styles/", data);
      // const res = await postApi("",
      //   {data},
      // );
      const res = await axios.post(
        `${BaseUrl()}/api/style/v1/bulk-create-styles/`,
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );
      if (res.status === 201) {
        showToast({
          message: `${data.length} styles saved successfully!`,
          type: "success",
        });

        // Clear form - same as reference
        clearAll();
        setCategoryId("");
        setMsrCategoryId("");
        setCategory(null);
        navigate("/style-management");
      }
    } catch (error) {
      console.error("Error saving bulk styles:", error);
      showToast({
        message: "Error saving styles. Please try again.",
        type: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClearAll = () => {
    if (rows.length > 0) {
      const shouldClear = window.confirm(
        "Are you sure you want to clear all data?"
      );
      if (shouldClear) {
        clearAll();
      }
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="text3xl">Bulk Create Styles</h1>
      </div>

      <p className="text-center font-bold text-3xl">
        Create multiple styles for a single category
      </p>

      <div className="page-content">
        {/* Category Selection */}
        <div className="bulk-category-section">
          <div className="category-header">
            <h3>Step 1: Select Category</h3>
            <p>All styles will be created under this category</p>
          </div>

          <SelectApparelCategory
            categoryId={categoryId}
            setCategoryId={setCategoryId}
            setCategory={setCategory}
            addCategory={true}
          />
        </div>

        {/* Bulk Table */}
        {category && (
          <div className="bulk-table-section">
            <div className="bulk-table-header">
              <div className="table-title">
                <h3>Step 2: Add Styles</h3>
                <span className="category-badge">
                  Category: {category?.name}
                </span>
              </div>
              <div className="table-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleClearAll}
                  disabled={rows.length === 0}
                >
                  <RotateCcw size={16} />
                  Clear All
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleAddRow}
                >
                  <Plus size={16} />
                  Add Style
                </button>
              </div>
            </div>

            {rows.length > 0 ? (
              <div className="bulk-table-container">
                <table className="bulk-styles-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Style Name</th>
                      <th>Style Number</th>
                      <th>Tags</th>
                      <th>Images</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => (
                      <BulkStyleRow
                        key={row.id}
                        data={row}
                        index={index}
                        onUpdate={(field, value) =>
                          updateRow(row.id, field, value)
                        }
                        onRemove={() => removeRow(row.id)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-table">
                <p>No styles added yet. Click "Add Style" to get started.</p>
              </div>
            )}

            {/* Save Actions */}
            {rows.length > 0 && (
              <div className="bulk-save-actions">
                <div className="save-info">
                  <span>{rows.length - 1} style(s) ready to create</span>
                </div>
                <button
                  type="button"
                  className="btn-save-bulk"
                  onClick={handleSaveAll}
                  disabled={!validateRows() || isSaving}
                >
                  <Save size={16} />
                  {isSaving
                    ? "Data is being sent to the server, please kindly wait patiently..."
                    : `Create ${rows.length - 1} Styles`}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
